import React, { useState, useEffect } from "react";
import {
    Paper,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    LinearProgress,
    Alert,
    Tooltip,
    Divider,
} from "@mui/material";
import SectionTitle from "../../common/SectionTitle";
import CurrentClientContext from "../../../providers/CurrentClientProvider";
import TipsContext from "../../../providers/GlobalTip";
import PosService from "../../../services/PosService";
import { trackEvent, handleTrackError } from "../../../services/AmplitudeService";
import constants from "../../../constants/constant";

function HeartlandCustomFieldSelector() {
    const currentClient = CurrentClientContext.useClient();
    const tip = TipsContext.useMessage();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [data, setData] = useState(null);
    const [selected, setSelected] = useState("");

    const client = currentClient.client;
    const isHeartlandRetail =
        client && Number(client.PointOfSaleId) === constants.POS_ID.HEARTLAND_RETAIL_API;

    useEffect(() => {
        if (!isHeartlandRetail) return;
        let cancelled = false;
        async function load() {
            setLoading(true);
            try {
                const res = await PosService.getHeartlandCustomFields(client.Id);
                if (cancelled) return;
                setData(res);
                setSelected(res?.selected_field || "");
            } catch (e) {
                if (cancelled) return;
                console.error(e);
                tip.show("Could not load Heartland custom fields");
                handleTrackError({
                    error: e,
                    pageName: "Client POS Mapping",
                    additionalMetadata: {
                        clientCode: client?.ClientCode,
                        action: "Load Heartland Custom Fields",
                    },
                });
            }
            if (!cancelled) setLoading(false);
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [client?.Id, isHeartlandRetail]);

    if (!isHeartlandRetail) return null;

    async function onChange(e) {
        const value = e.target.value || null;
        const previous = selected;
        setSelected(e.target.value);
        setSaving(true);
        try {
            await PosService.saveHeartlandCustomFieldSelection(client.Id, value);
            tip.showSuccess(
                value
                    ? `Mapping set to "${value}". Run Sync Departments to refresh codes.`
                    : "Mapping cleared."
            );
            trackEvent("Client POS Event", {
                action: "Set Heartland Custom Field Mapping",
                clientCode: client?.ClientCode,
                fieldName: value,
            });
        } catch (err) {
            setSelected(previous);
            tip.show("Could not save selection");
            handleTrackError({
                error: err,
                pageName: "Client POS Mapping",
                additionalMetadata: {
                    clientCode: client?.ClientCode,
                    action: "Save Heartland Custom Field Mapping",
                },
            });
        }
        setSaving(false);
    }

    function pctColor(pct) {
        if (pct >= 80) return "#5c4174";
        if (pct >= 30) return "#1d9ef4";
        if (pct > 0) return "#d97706";
        return "rgba(112, 119, 139, 1)";
    }

    function fieldTitle(f) {
        const parts = [
            `${f.matched.toLocaleString()} of ${(data?.total_products || 0).toLocaleString()} products match a class code`,
            `populated on ${f.populated_pct}% of products`,
            `${f.distinct_values.toLocaleString()} distinct values`,
        ];
        if (f.samples?.length) parts.push(`e.g. ${f.samples.join(" · ")}`);
        return parts.join(" — ");
    }

    function PctBadge({ pct }) {
        const color = pctColor(pct);
        return (
            <Box
                sx={{
                    flexShrink: 0,
                    minWidth: 52,
                    px: 1,
                    py: 0.25,
                    border: `1px solid ${color}`,
                    color,
                    borderRadius: 1,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    textAlign: "center",
                    bgcolor: `${color}10`,
                    lineHeight: 1.5,
                }}
            >
                {pct}%
            </Box>
        );
    }

    function renderSelectedRow(value) {
        if (!value) {
            return <em style={{ color: "rgba(112, 119, 139, 1)" }}>Not configured</em>;
        }
        const field = (data?.fields || []).find((f) => f.name === value);
        return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0, fontWeight: 500 }}>
                    {value}
                </Typography>
                {field && <PctBadge pct={field.match_pct} />}
            </Box>
        );
    }

    return (
        <Paper elevation={2} sx={{ mt: 2 }}>
            <SectionTitle type="primary" label="Heartland Department Field" />
            <Box sx={{ p: 2 }}>
                {loading || saving ? (
                    <LinearProgress
                        sx={{
                            mb: 1.5,
                            borderRadius: 1,
                            height: 3,
                            backgroundColor: "rgba(92,63,117,0.1)",
                            "& .MuiLinearProgress-bar": { backgroundColor: "primary.main" },
                        }}
                    />
                ) : null}

                {data && data.total_products === 0 ? (
                    <Alert severity="warning" sx={{ mb: 1.5, fontSize: "0.8rem", py: 0.5 }}>
                        No products synced yet. Run a Heartland product sync first.
                    </Alert>
                ) : null}

                {data && data.total_products > 0 && !data.has_class_codes ? (
                    <Alert severity="info" sx={{ mb: 1.5, fontSize: "0.8rem", py: 0.5 }}>
                        No class codes set up for this client yet, so every field shows 0%. Add the
                        client's classes first, then pick the field that matches them.
                    </Alert>
                ) : null}

                <FormControl fullWidth size="small" disabled={loading || saving || !data}>
                    <InputLabel id="heartland-custom-field-label">Custom field</InputLabel>
                    <Select
                        labelId="heartland-custom-field-label"
                        label="Custom field"
                        value={selected}
                        onChange={onChange}
                        renderValue={renderSelectedRow}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    maxHeight: 380,
                                    maxWidth: 460,
                                    boxShadow: "0 6px 24px rgba(32,35,43,0.12)",
                                    border: "1px solid rgba(92,63,117,0.12)",
                                    "& .MuiMenuItem-root": {
                                        py: 0.85,
                                        px: 1.5,
                                        borderBottom: "1px solid rgba(92,63,117,0.05)",
                                        "&:last-child": { borderBottom: "none" },
                                        "&:hover": {
                                            backgroundColor: "rgba(92,63,117,0.06)",
                                        },
                                        "&.Mui-selected": {
                                            backgroundColor: "rgba(92,63,117,0.12)",
                                            "&:hover": { backgroundColor: "rgba(92,63,117,0.18)" },
                                        },
                                    },
                                },
                            },
                        }}
                    >
                        <MenuItem value="">
                            <em style={{ color: "rgba(112, 119, 139, 1)" }}>None</em>
                        </MenuItem>
                        {(data?.fields || []).length > 0 && <Divider component="li" />}
                        {(data?.fields || []).map((f) => {
                            const showSampleInline = f.match_pct >= 30 && f.samples.length > 0;
                            const sample = f.samples[0] || "";
                            const sampleShort = sample.length > 32 ? sample.slice(0, 30) + "…" : sample;
                            return (
                                <MenuItem key={f.name} value={f.name}>
                                    <Tooltip
                                        title={fieldTitle(f)}
                                        placement="left"
                                        enterDelay={400}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", minWidth: 0 }}>
                                            <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                                                <Typography
                                                    variant="body2"
                                                    noWrap
                                                    sx={{ fontWeight: 500, lineHeight: 1.3, color: "text.primary" }}
                                                >
                                                    {f.name}
                                                </Typography>
                                                {showSampleInline && (
                                                    <Typography
                                                        variant="caption"
                                                        noWrap
                                                        sx={{
                                                            display: "block",
                                                            color: "text.secondary",
                                                            fontSize: "0.7rem",
                                                            lineHeight: 1.3,
                                                        }}
                                                    >
                                                        e.g. {sampleShort}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <PctBadge pct={f.match_pct} />
                                        </Box>
                                    </Tooltip>
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
            </Box>
        </Paper>
    );
}

export default HeartlandCustomFieldSelector;
